import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { brandFor } from "../businessBranding";
import { cancelTransfer, pendingTransferFor } from "../notifications/api";
import { transfersChanged } from "../notifications/useIncomingTransferCount";
import TransferOrderModal from "./TransferOrderModal";
import DateField from "./DateField";
import { UNPAID, paidMethodsFor } from "../paymentOptions";
import { commissionOf, estimateFdCommission, pctLabel } from "../commission";
import "../order-detail-panel.css";
import "../notifications.css";

/* ------------------------------------------------------------------ *
 * OrderDetailPanel — the work order detail, as a panel over the list.
 *
 * Replaces the `order-side-slide` markup in components.js:
 *   - a grey title bar of icon buttons -> a proper header with the name
 *     first, status inline, and named actions
 *   - two flat key/value columns       -> Service vs Order & payment
 *   - a products table                 -> per-product assignment + state
 *   - (new)                           -> a production timeline
 *
 * Presentational: the caller owns which order is open and what the
 * actions do. `order` is the row shape built by workOrdersRedesign.
 * ------------------------------------------------------------------ */

const TIMELINE = [
  { label: "Order received", key: "received" },
  { label: "Arranging", key: "arranging" },
  { label: "Checked", key: "checked" },
  { label: "Delivered", key: "delivered" },
];

const STATE_TONE = {
  Checked: "ok",
  Arranging: "warn",
  "Not started": "idle",
};

/* ---- audit history ----
   GET /work-orders/history/:id — newest first, `limit` per page (max 500).
   Each entry is one write: created / updated / deleted / added_to_run /
   removed_from_run, with the actor from the JWT and, for updates, the
   list of fields that changed, and — for the transfer_* / transferred_*
   actions — a `transfer` block naming the two businesses. Mapped here into
   the timeline nodes. */
const HISTORY_PAGE = 100;

export const ACTION_LABEL = {
  created: "Order created",
  updated: "Order edited",
  deleted: "Order deleted",
  added_to_run: "Added to delivery run",
  removed_from_run: "Removed from delivery run",
  transfer_requested: "Transfer requested",
  transfer_cancelled: "Transfer request cancelled",
  transfer_rejected: "Transfer declined",
  transferred_out: "Transferred out",
  transferred_in: "Received by transfer",
};

export const ACTION_KIND = {
  created: "create",
  updated: "update",
  deleted: "delete",
  added_to_run: "run",
  removed_from_run: "run",
  transfer_requested: "transfer",
  transfer_cancelled: "transfer",
  transfer_rejected: "transfer",
  transferred_out: "transfer",
  transferred_in: "transfer",
};

/* The two businesses on a transfer history entry, either side of it. */
const transferParty = (entry, side) => {
  const t = (entry && entry.transfer) || {};
  return (
    (side === "to" ? t.to_business_name : t.from_business_name) ||
    "another business"
  );
};

/* Friendlier names for the raw document fields an update reports. Anything
   not listed falls back to the snake_case key with underscores spaced out. */
const FIELD_LABEL = {
  order_state: "Status",
  type: "Type",
  serial_no: "Serial",
  late_person_name: "Late person",
  company_name: "Company",
  recipient_name: "Recipient",
  recipient_phone: "Recipient phone",
  ordered_by: "Ordered by",
  invoice_number: "Invoice",
  payment_option: "Payment method",
  payment_date: "Payment date",
  funeral_date: "Funeral date",
  funeral_time: "Funeral time",
  funeral_location: "Funeral location",
  casket_cover_delivery_time: "Casket cover time",
  casket_cover_delivery_location: "Casket cover delivery",
  tribuite_delivery_location: "Tribute delivery",
  tribute_delivery_location: "Tribute delivery",
  time_of_delivery: "Delivery time",
  delivery_location: "Delivery location",
  delivery_area: "Delivery area",
  delivery_date: "Delivery date",
  frequency: "Frequency",
  fd_code: "Funeral director",
  funeral_director: "Funeral director",
  casket_cover_selected: "Casket cover",
  tributes_selected: "Tributes",
  products_selected: "Products",
  price_detail: "Price",
  "price_detail.gst": "GST",
  "price_detail.total": "Total",
  "price_detail.sub_total": "Subtotal",
  "price_detail.subtotal": "Subtotal",
  "price_detail.discount": "Discount",
  "price_detail.delivery": "Delivery fee",
  notes: "Notes",
  message: "Message",
};

export const fieldLabel = (field) => {
  const key = String(field || "");
  if (FIELD_LABEL[key]) return FIELD_LABEL[key];
  const [head, ...rest] = key.split(".");
  const base = FIELD_LABEL[head] || head.replace(/_/g, " ");
  const tail = rest.join(".").replace(/_/g, " ");
  const pretty = tail ? `${base} · ${tail}` : base;
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

/* A changed value, short enough for one line. JSON-ish fields (locations,
   product lists) are collapsed to a name or an item count. */
const shortValue = (v) => {
  if (v === null || v === undefined || v === "") return "empty";
  if (typeof v === "string") {
    const t = v.trim();
    if (/^[[{]/.test(t)) {
      try {
        return shortValue(JSON.parse(t));
      } catch (_) {
        /* not JSON after all — fall through to the plain string */
      }
    }
    return t.length > 60 ? `${t.slice(0, 57)}…` : t;
  }
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") {
    if (v.name) return String(v.name);
    if (v.code) return String(v.code);
    const keys = Object.keys(v);
    return keys.length ? `${keys.length} field${keys.length === 1 ? "" : "s"}` : "empty";
  }
  return String(v);
};

const pad2 = (n) => String(n).padStart(2, "0");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/* "07 Sep 2026 · 14:12" — same shape the rest of the panel uses. */
const fmtWhen = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || "—");
  return `${pad2(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()} · ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
};

const runSummary = (run, verb) => {
  if (!run) return "";
  const who = [run.drivers_name, run.vehicle_number].filter(Boolean).join(" · ");
  const when = run.delivery_date ? ` for ${run.delivery_date}` : "";
  return `${verb} ${who ? `the run driven by ${who}` : "a delivery run"}${when}`;
};

/* One API entry -> one timeline node. */
function toEvent(entry) {
  const action = entry.action || "updated";
  const actor = entry.actor || {};
  const system = entry.source === "system" || !actor.user_id;
  let summary = "";
  /* Field edits render one per line under the card, not as a run-on string. */
  let changes = [];
  if (action === "updated") {
    changes = (Array.isArray(entry.changes) ? entry.changes : []).map((c) => ({
      field: fieldLabel(c.field),
      from: shortValue(c.from),
      to: shortValue(c.to),
    }));
    if (!changes.length) summary = "Saved with no field changes";
  } else if (action === "created") {
    summary = entry.work_order_serial ? `Serial ${entry.work_order_serial}` : "";
  } else if (action === "deleted") {
    summary = "The order was removed. Its details are kept in this record.";
  } else if (action === "added_to_run") {
    summary = runSummary(entry.run, "Added to");
  } else if (action === "removed_from_run") {
    summary = runSummary(entry.run, "Removed from");
  } else if (action === "transfer_requested") {
    summary = `${transferParty(entry, "to")} was asked to take this order`;
  } else if (action === "transfer_cancelled") {
    summary = `The request to ${transferParty(entry, "to")} was withdrawn`;
  } else if (action === "transfer_rejected") {
    summary = `${transferParty(entry, "to")} declined to take this order`;
  } else if (action === "transferred_out") {
    summary = `The order now belongs to ${transferParty(entry, "to")}`;
  } else if (action === "transferred_in") {
    summary = `The order came across from ${transferParty(entry, "from")}`;
  }
  return {
    id: entry._id,
    at: fmtWhen(entry.created_at),
    actor: system ? "System" : actor.full_name || actor.userName || "Unknown user",
    system,
    action: ACTION_LABEL[action] || fieldLabel(action),
    summary,
    changes,
    kind: ACTION_KIND[action] || "update",
    method: entry.request && entry.request.path ? entry.request.path : "",
    /* Which business recorded this entry. Present on every entry the
       server sends; null only from an older server, which is handled by
       the fallback in buildHistoryNodes. */
    business: entry.business || null,
  };
}

/* The trail can span businesses: once an order is transferred, the new
   owner reads the former owner's entries as well as its own.
   The list arrives newest first, so a section's marker is emitted BELOW
   its entries — bottom-up the timeline then reads as chronology:
   "Originally at Rays Florist", its entries, "Transferred to The Floral
   Decorator", its entries. The bottom marker is where the order began,
   unless there are older pages still unloaded, in which case it makes no
   claim about the beginning. */
const buildHistoryNodes = (events, fallbackBusiness, complete) => {
  const keyOf = (e) =>
    (e.business && (e.business.id || e.business.code || e.business.name)) || "self";
  const nodes = [];
  let i = 0;
  while (i < events.length) {
    const key = keyOf(events[i]);
    const group = [];
    while (i < events.length && keyOf(events[i]) === key) {
      group.push(events[i]);
      i += 1;
    }
    group.forEach((e) => nodes.push({ nodeType: "event", ...e }));

    const oldest = group[group.length - 1];
    const isOrigin = i >= events.length && complete;
    const biz = oldest.business || fallbackBusiness || {};
    const brand = brandFor(biz);
    nodes.push({
      nodeType: "business",
      eyebrow: isOrigin
        ? oldest.kind === "create"
          ? "Created at"
          : "Originally at"
        : i >= events.length
        ? "Recorded at"
        : "Transferred to",
      business: {
        name: biz.name || biz.code || "This business",
        logo: brand.logo,
        monogram: brand.monogram,
      },
    });
  }
  return nodes;
};

/* `transferred_from` only means something once the order has actually
   moved: mongoose hands back an empty object for every order that never
   did, so a bare existence check would badge the whole list. */
export function transferProvenance(order) {
  const from = (order && order.transferred_from) || null;
  if (!from || !(from.business_name || from.business_code || from.business_id)) {
    return null;
  }
  return from;
}

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/* The "Paid" button's question: how was it paid, and when (today unless
   told otherwise). `onConfirm(method, date)` returns a promise; the dialog
   stays open, with the choice intact, if it rejects. */
function MarkPaidDialog({ order, onCancel, onConfirm }) {
  const methods = paidMethodsFor(order.type);
  const [method, setMethod] = useState("");
  const [date, setDate] = useState(() => moment().format("DD/MM/YYYY"));
  const [busy, setBusy] = useState(false);

  function submit() {
    if (!method || !date || busy) return;
    setBusy(true);
    Promise.resolve(onConfirm(method, date))
      .then(onCancel)
      .catch(() => setBusy(false));
  }

  return (
    <div className="od-paid-scrim" onClick={busy ? undefined : onCancel}>
      <div
        className="od-paid"
        role="dialog"
        aria-labelledby="od-paid-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div id="od-paid-title" className="rf-serif od-paid-title">
          Mark as paid
        </div>
        <div className="od-paid-sub">
          {order.serial} · {order.name}
        </div>

        <div className="rf-label od-paid-label">Payment method</div>
        <div className="od-paid-methods" role="radiogroup">
          {methods.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={method === m}
              className={`od-paid-method${method === m ? " is-on" : ""}`}
              onClick={() => setMethod(m)}
            >
              {m}
            </button>
          ))}
        </div>

        <div className="rf-label od-paid-label">Payment date</div>
        <div className="od-paid-date">
          <DateField value={date} onChange={setDate} />
        </div>

        <div className="od-paid-actions">
          <button
            type="button"
            className="rf-btn rf-btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rf-btn od-btn-paid"
            onClick={submit}
            disabled={!method || !date || busy}
          >
            {busy ? "Saving…" : "Mark as paid"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrderDetailPanel({
  order,
  onClose,
  onEdit,
  onPrint,
  onDelete,
  deleteLoading,
  /* (method, "DD/MM/YYYY") => Promise — given only to roles that can write;
     the "Paid" button shows while the order is unpaid */
  onMarkPaid,
  /* Both gate on the caller's `workorders:transfer` check; the server
     re-checks either way. `onTransferChange` lets the list refresh after a
     request goes out or is withdrawn. */
  canTransfer = false,
  onTransferChange,
}) {
  const navigate = useNavigate();
  /* "detail" | "history" — History swaps the body, header stays for context */
  const [view, setView] = useState("detail");
  const [paidOpen, setPaidOpen] = useState(false);

  /* Esc closes — the old side slide could only be dismissed by its × —
     or, with the Paid dialog up, just the dialog */
  useEffect(() => {
    function onKey(e) {
      if (e.key !== "Escape") return;
      if (paidOpen) setPaidOpen(false);
      else onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, paidOpen]);

  const orderId = order ? order.id : null;
  useEffect(() => setPaidOpen(false), [orderId]);

  /* ---- outgoing transfer state ----
     One unpaged fetch of the pending outgoing requests answers "is this
     order on its way out?" (docs §7). Only worth asking when the user can
     act on the answer. */
  const [transfer, setTransfer] = useState(null);
  const [txModal, setTxModal] = useState(false);
  const [txBusy, setTxBusy] = useState(false);

  useEffect(() => {
    setTransfer(null);
    setTxModal(false);
    if (!canTransfer || !orderId) return undefined;
    let cancelled = false;
    pendingTransferFor(orderId)
      .then((t) => !cancelled && setTransfer(t))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [orderId, canTransfer]);

  const onRequested = (t) => {
    setTransfer(t);
    setTxModal(false);
    const to = (t && t.to_business && t.to_business.name) || "the other business";
    toast.success(`${to} has been asked to take this order.`);
    transfersChanged();
    if (onTransferChange) onTransferChange();
  };

  const withdraw = () => {
    if (!transfer || txBusy) return;
    setTxBusy(true);
    cancelTransfer(transfer._id)
      .then(() => {
        setTransfer(null);
        toast.success("Transfer request withdrawn.");
        transfersChanged();
        if (onTransferChange) onTransferChange();
      })
      .catch((e) => {
        const res = (e && e.response) || {};
        if (res.status === 404) {
          // Decided while the panel was open — the list knows better.
          setTransfer(null);
          toast.warn("That request was already decided by the other business.");
          if (onTransferChange) onTransferChange();
          return;
        }
        toast.error((res.data && res.data.message) || "Could not withdraw the request.");
      })
      .finally(() => setTxBusy(false));
  };

  /* History is fetched lazily — the first time the tab is opened for this
     order — and paged newest-first. `done` flips once a page comes back
     short, so "Load more" disappears at the end of the trail. */
  const [hist, setHist] = useState({
    entries: [],
    page: 0,
    done: false,
    loading: false,
    error: "",
  });

  /* A different order always opens on its details, not the last tab, and
     starts with a clean history so the last order's trail never shows. */
  useEffect(() => {
    setView("detail");
    setHist({ entries: [], page: 0, done: false, loading: false, error: "" });
  }, [orderId]);

  const loadHistory = useCallback(
    (page) => {
      if (!orderId) return;
      setHist((h) => ({ ...h, loading: true, error: "" }));
      axios
        .get(`${BASE_SERVER_URL}/work-orders/history/${orderId}`, {
          params: { page, limit: HISTORY_PAGE },
        })
        .then((res) => {
          const body = res.data || {};
          if (body.status !== "success") throw new Error(body.message || "failed");
          const rows = Array.isArray(body.response) ? body.response : [];
          setHist((h) => ({
            entries: page === 1 ? rows : h.entries.concat(rows),
            page,
            done: rows.length < HISTORY_PAGE,
            loading: false,
            error: "",
          }));
        })
        .catch((err) => {
          const msg =
            (err && err.response && err.response.data && err.response.data.message) ||
            "Could not load the history for this order.";
          setHist((h) => ({ ...h, loading: false, error: msg }));
        });
    },
    [orderId]
  );

  useEffect(() => {
    if (view === "history" && hist.page === 0 && !hist.loading && !hist.error) {
      loadHistory(1);
    }
  }, [view, hist.page, hist.loading, hist.error, loadHistory]);

  /* Only the fallback for entries an older server sent without a
     recording business of their own. */
  const activeBusiness = useActiveBusiness();

  if (!order) return null;

  const events = hist.entries.map(toEvent);
  const histNodes = buildHistoryNodes(events, activeBusiness, hist.done);

  const cameFrom =
    order.transferredFrom || transferProvenance(order.raw) || null;

  const d = order.detail || {};
  const products = d.products || [];
  /* the sash is charged once per line, as on the order form */
  const lineTotal = (p) =>
    (Number(p.price) || 0) * (Number(p.amount) || 0) + (Number(p.sashPrice) || 0);
  const total = products.reduce((sum, p) => sum + lineTotal(p), 0);

  /* The breakdown the review step shows before an order is saved, rebuilt
     from the saved price_detail. The product sum is GST-inclusive, GST is
     the component inside it and delivery is added on top — the same rule
     as the order form and components.js PriceTag. */
  const raw = order.raw || {};
  const priceDetail = raw.price_detail || {};
  const gstStored = Number(priceDetail.gst);
  const gstRate = gstStored > 0 ? (gstStored > 1 ? gstStored / 100 : gstStored) : 0.1;
  const savedProducts = Number(priceDetail.total_price);
  const productsTotal = savedProducts > 0 ? savedProducts : total;
  const feeValue =
    priceDetail.delivery_fee === "Custom Delivery Fee"
      ? Number(priceDetail.custom_delivery_fee) || 0
      : Number(priceDetail.delivery_fee) || 0;
  const exGst = productsTotal / (1 + gstRate);
  const grandTotal = productsTotal + feeValue;
  const commission = commissionOf(raw);
  const commissionAmount = commission.apply
    ? estimateFdCommission(productsTotal, feeValue, commission.pct)
    : 0;
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const isUnpaid = !raw.payment_option || raw.payment_option === UNPAID;
  const paymentValue = raw.payment_option
    ? `${raw.payment_option}${raw.payment_date ? ` · paid ${raw.payment_date}` : ""}`
    : "No payment method selected";

  return (
    <div className="od-wrap">
      <div className="od-scrim" onClick={onClose} />

      <aside className="od-panel" role="dialog" aria-label={`Work order ${order.serial}`}>
        <header className="od-head">
          <div className="od-head-main">
            <div className="od-meta">
              <span className="od-serial rf-num">{order.serial}</span>
              <span className={`od-type is-${order.type.toLowerCase()}`}>{order.type}</span>
              <span className="od-status">
                <span className="od-status-dot" style={{ background: order.statusColor }} />
                {order.status}
              </span>
            </div>
            <h2 className="rf-serif od-name">{order.name}</h2>
            <div className="od-sub">{d.provenance}</div>
            {/* Came from another business: explains the foreign serial
                prefix on the line above it. */}
            {cameFrom && (
              <span className="od-from" title={
                cameFrom.transferred_at ? `Received ${fmtWhen(cameFrom.transferred_at)}` : undefined
              }>
                Transferred from {cameFrom.business_name || cameFrom.business_code || "another business"}
              </span>
            )}
          </div>

          <div className="od-actions">
            {onMarkPaid && isUnpaid && (
              <button
                type="button"
                className="rf-btn od-btn-paid"
                onClick={() => setPaidOpen(true)}
                title="Record how and when this order was paid"
              >
                Paid
              </button>
            )}
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={() => setView(view === "history" ? "detail" : "history")}
            >
              {view === "history" ? "\u2190 Details" : "History"}
            </button>
            {canTransfer && !transfer && (
              <button
                type="button"
                className="rf-btn rf-btn-ghost"
                onClick={() => setTxModal(true)}
                title="Send this order to another business"
              >
                Transfer
              </button>
            )}
            {onPrint && (
              <button type="button" className="rf-btn rf-btn-ghost" onClick={onPrint}>
                Print
              </button>
            )}
            {onEdit && (
              <button type="button" className="rf-btn rf-btn-primary" onClick={onEdit}>
                Edit order
              </button>
            )}
            <button type="button" className="rf-icon-btn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>

        {/* ---------- pending transfer out ---------- *
            While a request is awaiting approval the order is still ours and
            still editable — the banner says so, and offers to withdraw. */}
        {transfer && (
          <div className="od-txbanner">
            <span className="od-txbanner-dot" aria-hidden="true" />
            <div className="od-txbanner-text">
              <b>
                Transfer pending &rarr;{" "}
                {(transfer.to_business && transfer.to_business.name) || "another business"}
              </b>
              <span>
                Waiting for them to accept. The order stays here, and stays editable,
                until they do.
              </span>
            </div>
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={() => navigate(`/transfers/${transfer._id}`)}
            >
              View request
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-danger"
              onClick={withdraw}
              disabled={txBusy}
            >
              {txBusy ? "Withdrawing\u2026" : "Withdraw"}
            </button>
          </div>
        )}

        {/* ---------- history view: who did what, newest first ---------- */}
        {view === "history" && (
          <div className="od-body od-history">
            <div className="od-hist-intro">
              Every change to this order, newest first.
              {events.length > 0 && (
                <span className="od-hist-count rf-num">
                  {events.length}
                  {hist.done ? "" : "+"} {events.length === 1 ? "entry" : "entries"}
                </span>
              )}
            </div>

            {hist.error && (
              <div className="od-hist-state is-error">
                {hist.error}
                <button
                  type="button"
                  className="rf-btn rf-btn-ghost od-hist-retry"
                  onClick={() => loadHistory(hist.page || 1)}
                >
                  Try again
                </button>
              </div>
            )}
            {!hist.error && hist.loading && events.length === 0 && (
              <div className="od-hist-state">Loading history…</div>
            )}
            {!hist.error && !hist.loading && hist.page > 0 && events.length === 0 && (
              <div className="od-hist-state">
                No history recorded for this order yet.
              </div>
            )}

            <div className="od-hist-list" hidden={events.length === 0}>
              {histNodes.map((n, i) => {
                const line = i < histNodes.length - 1 && (
                  <span className="od-hist-line" />
                );
                if (n.nodeType === "business") {
                  return (
                    <div key={`biz-${i}`} className="od-hist-node is-business">
                      <div className="od-hist-track">
                        <span className="od-hist-bizchip">
                          {n.business.logo ? (
                            <img src={n.business.logo} alt="" />
                          ) : (
                            <span>{n.business.monogram || initials(n.business.name)}</span>
                          )}
                        </span>
                        {line}
                      </div>
                      <div className="od-hist-card od-hist-bizcard">
                        <div className="od-hist-bizeyebrow">{n.eyebrow}</div>
                        <div className="rf-serif od-hist-bizname">
                          {n.business.name}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={n.id || `${n.at}-${i}`} className={`od-hist-node is-${n.kind}`}>
                    <div className="od-hist-track">
                      <span className="od-hist-dot" />
                      {line}
                    </div>
                    <div className="od-hist-card">
                      <div className="od-hist-toprow">
                        <span className="od-hist-action">{n.action}</span>
                        <span className="od-hist-when rf-num">{n.at}</span>
                      </div>
                      <div className="od-hist-who">
                        <span className={`od-hist-chip${n.system ? " is-system" : ""}`}>
                          {n.system ? "⚙" : initials(n.actor)}
                        </span>
                        {n.actor}
                      </div>
                      {n.summary && (
                        <div className="od-hist-summary">{n.summary}</div>
                      )}
                      {n.changes && n.changes.length > 0 && (
                        <ul className="od-hist-changes">
                          {n.changes.map((c, ci) => (
                            <li key={ci}>
                              <span className="od-hist-field">{c.field}</span>
                              <span className="od-hist-diff">
                                {c.from} <span className="od-hist-arrow">→</span> {c.to}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!hist.error && !hist.done && hist.page > 0 && (
              <button
                type="button"
                className="rf-btn rf-btn-ghost od-hist-more"
                disabled={hist.loading}
                onClick={() => loadHistory(hist.page + 1)}
              >
                {hist.loading ? "Loading…" : "Load older entries"}
              </button>
            )}
          </div>
        )}

        {view === "detail" && (
        <div className="od-body">
          {/* ---------- production timeline ---------- */}
          <div className="od-timeline">
            {TIMELINE.map((t, i) => {
              const step = (d.timeline || {})[t.key] || {};
              const state = step.done ? "done" : step.active ? "active" : "todo";
              return (
                <div key={t.key} className={`od-step is-${state}`}>
                  <div className="od-step-track">
                    <span className="od-step-dot" />
                    {i < TIMELINE.length - 1 && <span className="od-step-line" />}
                  </div>
                  <div className="od-step-label">{t.label}</div>
                  <div className="od-step-time rf-num">{step.at || "—"}</div>
                </div>
              );
            })}
          </div>

          {/* ---------- facts ---------- */}
          <div className="od-facts">
            {[
              { title: "Service", rows: d.service || [] },
              { title: "Order & payment", rows: d.payment || [] },
            ].map((sec) => (
              <section key={sec.title}>
                <div className="rf-label od-facts-label">{sec.title}</div>
                <div className="od-factcard">
                  {sec.rows.map((r) => (
                    <div key={r.k} className="od-fact">
                      <span className="od-fact-k">{r.k}</span>
                      <span className={`od-fact-v${r.tone ? ` is-${r.tone}` : ""}`}>
                        {r.v}
                        {r.sub && <span className="od-fact-sub rf-num">{r.sub}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* ---------- products ---------- */}
          <section className="od-products">
            <div className="od-products-head">
              <span className="rf-label">Products · {products.length} items</span>
              {total > 0 && (
                <span className="od-products-total">
                  Total <b className="rf-num">${total.toFixed(2)}</b>
                </span>
              )}
            </div>

            <div className="od-productcard">
              {products.length === 0 && (
                <div className="od-fact">
                  <span className="od-fact-v is-muted">No products saved on this order.</span>
                </div>
              )}
              {products.map((p, i) => (
                <div key={`${p.code || p.type}-${i}`} className="od-product">
                  <div className="od-product-row">
                  <span className="od-product-accent" style={{ background: p.color }} />
                  <div className="od-product-text">
                    <div className="od-product-name">{p.type}</div>
                    <div className="od-product-meta rf-num">
                      {[p.code, p.size].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <span className="od-product-qty rf-num">×{p.amount}</span>
                  <span className="od-product-price rf-num">
                    {money(lineTotal(p))}
                  </span>
                  </div>
                  {/* how the line total is made up: unit price × qty, plus
                      the sash (charged once per line, as on the order form) */}
                  {(p.amount > 1 || p.sashPrice > 0) && (
                    <div className="od-product-calc rf-num">
                      {p.amount} × {money(p.price)} each
                      {p.sashPrice > 0 && ` + sash ${money(p.sashPrice)}`}
                    </div>
                  )}
                  {(p.sash || p.sashPrice > 0 || p.comment || p.lidType) && (
                    <div className="od-product-notes">
                      {p.lidType && (
                        <div className="od-product-note">
                          <span className="od-product-note-k">Lid</span>
                          <span className="od-product-note-v">{p.lidType}</span>
                        </div>
                      )}
                      {(p.sash || p.sashPrice > 0) && (
                        <div className="od-product-note is-text">
                          <span className="od-product-note-k">Sash</span>
                          {/* its price is in the line's breakdown above */}
                          <span className="od-product-note-v">
                            {p.sash || <em>no wording yet</em>}
                          </span>
                        </div>
                      )}
                      {p.comment && (
                        <div className="od-product-note is-text is-comment">
                          <span className="od-product-note-k">Comment</span>
                          <span className="od-product-note-v">{p.comment}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* progress sits quietly in the card's bottom-right corner */}
                  <div className="od-product-foot">
                    {p.assigned && (
                      <span className="od-product-who">{p.assigned}</span>
                    )}
                    <span className={`od-product-state is-${STATE_TONE[p.state] || "idle"}`}>
                      {p.state}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ---------- order total, as on the review step ---------- */}
          {(productsTotal > 0 || feeValue > 0) && (
            <section className="od-total">
              <div className="od-total-label">Order total</div>
              {[
                { k: `Products (${products.length}) ex GST`, v: money(exGst) },
                { k: `GST (${Math.round(gstRate * 100)}%)`, v: money(exGst * gstRate) },
                { k: "Delivery fee", v: money(feeValue) },
              ].map((t) => (
                <div key={t.k} className="od-total-row">
                  <span>{t.k}</span>
                  <span className="rf-num">{t.v}</span>
                </div>
              ))}
              <div className="od-total-row is-total">
                <span>Total incl. GST</span>
                <span className="rf-num">{money(grandTotal)}</span>
              </div>
              {commission.apply && (
                <div
                  className="od-total-row is-commission"
                  title="Paid to the funeral director out of revenue — the customer's total is unchanged"
                >
                  <span>FD commission ({pctLabel(commission.pct)})</span>
                  <span className="rf-num">{money(commissionAmount)}</span>
                </div>
              )}
              {commission.apply && (
                <div className="od-total-row is-net">
                  <span>Total after commission</span>
                  <span className="rf-num">{money(grandTotal - commissionAmount)}</span>
                </div>
              )}
              <div className="od-payment">
                <div className="od-payment-label">Payment</div>
                <div className="od-payment-value">
                  {raw.payment_option === "**UNPAID**" ? (
                    <span className="od-unpaid">UNPAID</span>
                  ) : (
                    paymentValue
                  )}
                </div>
              </div>
            </section>
          )}

          <div className="od-foot">
            <span>
              {d.created ? `Created ${d.created}` : ""}
              {d.discardOn ? ` · discarded on ${d.discardOn}` : ""}
            </span>
            {onDelete && (
              <button type="button" className="od-delete" onClick={onDelete} disabled={deleteLoading}>
                {deleteLoading ? "Deleting…" : "Delete order"}
              </button>
            )}
          </div>
        </div>
        )}
      </aside>

      {paidOpen && (
        <MarkPaidDialog
          order={order}
          onCancel={() => setPaidOpen(false)}
          onConfirm={onMarkPaid}
        />
      )}

      {txModal && (
        <TransferOrderModal
          order={order}
          onClose={() => setTxModal(false)}
          onRequested={onRequested}
        />
      )}
    </div>
  );
}

export default OrderDetailPanel;
